import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog"
import { Button } from "@/components/button"
import { toast } from "sonner"
import { useEffect } from "react"
import { Input } from "@/components/ui/input"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useRenameFlashcardModal } from "@/modules/flashcards/stores/use-rename-flashcard-modal-store"
import useUpdateDeckSet from "@/modules/flashcards/queries/use-update-deck-set"
import {
  renameFlashcardSchema,
  type RenameFlashcardSchemaType,
} from "@/modules/flashcards/validations/rename-flashcard"

const RenameFlashcardModal = () => {
  const { isOpen, flashcardToRename, closeModal } = useRenameFlashcardModal()
  const { isPending, mutateAsync } = useUpdateDeckSet()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RenameFlashcardSchemaType>({
    resolver: zodResolver(renameFlashcardSchema),
    defaultValues: { name: "" },
  })

  useEffect(() => {
    if (flashcardToRename && flashcardToRename.name) {
      reset({ name: flashcardToRename.name })
    }
  }, [flashcardToRename, reset])

  const handleOnClose = () => {
    if (isPending) return
    closeModal()
  }

  const handleOnEdit = async (data: RenameFlashcardSchemaType) => {
    if (!flashcardToRename) return

    try {
      const body = { name: data.name.trim() }
      await mutateAsync({ flashcardId: flashcardToRename.id, body })
      toast.success("La flashcard ha sido editada correctamente.")
    } catch {
      toast.error("Ha ocurrido un error al editar la flashcard.")
    } finally {
      closeModal()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOnClose}>
      <DialogContent className="w-[95%] max-w-[500px]">
        <DialogHeader className="flex items-start">
          <DialogTitle>Renombrar flashcard</DialogTitle>
          <DialogDescription className="w-full">
            <form className="mt-1 w-full" onSubmit={handleSubmit(handleOnEdit)}>
              <Input
                placeholder="Nombre de la flashcard"
                {...register("name")}
                className="rounded-[8px] text-black"
              />
              {errors.name && (
                <span className="mt-2 block text-xs text-red-600">{errors.name.message}</span>
              )}
            </form>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-row items-center justify-end gap-1">
          <DialogClose asChild>
            <Button type="button" className="bg-gray-300 text-black" disabled={isPending}>
              Cancelar
            </Button>
          </DialogClose>

          <Button
            type="submit"
            onClick={handleSubmit(handleOnEdit)}
            loading={isPending}
            disabled={isPending}
          >
            Renombrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default RenameFlashcardModal
